import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../config';
import { ColumnMapping, MasterFilterKey, MasterSearchKey } from '../../../../../types/master';
import { type ItemListType } from '../../../../../types/common';
import { renameMasterFieldMapping } from '../../../function/handleMasterVerification';

/**
 * item_list から item_id → 表示ラベルのマップを作る（#2905）。
 * ラベル規則は MasterMappingFormModal の patternItems と揃える:
 *   - 子あり: 親に紐づく子ごとに `${親} > ${子}`
 *   - 子なし: 親 `${親}`
 */
const buildItemLabelMap = (itemList: ItemListType[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const item of itemList) {
    const children = item.children ?? [];
    if (children.length > 0) {
      for (const child of children) {
        map.set(child.id, `${item.parentValue} > ${child.childValue}`);
      }
    } else {
      map.set(item.id, item.parentValue);
    }
  }
  return map;
};

/**
 * パターンの項目名（item_list）が変更された際に、そのパターンを参照している
 * 全マスタマッピングの保持ラベル（search_item_label / scope_item_label /
 * column_mappings[].item_label）を最新の項目名に更新する（#2905）。
 *
 * 照合・反映ロジックは item_id を基準に動くため、label はあくまで表示用。
 * ただし古い名前が UI に残ると混乱するため、保存値も最新名へ同期する。
 * item_id がマップに見つからない（項目自体が削除された）場合はラベルを変更しない。
 *
 * @param patternId   更新されたパターンID
 * @param itemList    更新後の item_list（camelCase）
 * @param patternName 更新後のパターン名（省略時は pattern_name を更新しない）
 */
export const syncMasterMappingLabels = async (
  patternId: string,
  itemList: ItemListType[],
  patternName?: string,
): Promise<void> => {
  if (!patternId) return;

  const labelMap = buildItemLabelMap(itemList);

  const mappingsSnap = await getDocs(
    query(collectionGroup(db, 'master_mappings'), where('pattern_id', '==', patternId)),
  );
  if (mappingsSnap.empty) return;

  const batch = writeBatch(db);
  let hasUpdate = false;
  // Cloud SQL(master_field_mappings)の source_field_name = search_item_label。
  // 照合キーの項目名が変わった分は Postgres 側もリネームする（#2905）。
  const sourceFieldRenames: { old_source_field_name: string; new_source_field_name: string }[] = [];

  for (const mappingDoc of mappingsSnap.docs) {
    const data = mappingDoc.data();
    const updates: Record<
      string,
      string | ColumnMapping[] | MasterFilterKey[] | MasterSearchKey | Timestamp
    > = {};

    // 照合キー（search_item_id）のラベル
    const searchItemId = data.search_item_id as string | undefined;
    if (searchItemId) {
      const latest = labelMap.get(searchItemId);
      const current = data.search_item_label as string | undefined;
      if (latest !== undefined && latest !== current) {
        updates.search_item_label = latest;
        if (current) {
          sourceFieldRenames.push({
            old_source_field_name: current,
            new_source_field_name: latest,
          });
        }
      }
    }

    // 検索キー（search_key）のラベル（#3180）。
    // 旧フィールドのミラーなので、search_item_label と同じ内容に揃える。
    const searchKey = data.search_key as MasterSearchKey | undefined;
    if (searchKey?.item_id) {
      const latest = labelMap.get(searchKey.item_id);
      if (latest !== undefined && latest !== searchKey.item_label) {
        updates.search_key = { ...searchKey, item_label: latest };
      }
    }

    // 絞り込みキー（filter_keys）のラベル（#3180）
    const filterKeys = (data.filter_keys as MasterFilterKey[] | undefined) ?? [];
    let filterKeysChanged = false;
    const nextFilterKeys = filterKeys.map(key => {
      const latest = key.item_id ? labelMap.get(key.item_id) : undefined;
      if (latest !== undefined && latest !== key.item_label) {
        filterKeysChanged = true;
        return { ...key, item_label: latest };
      }
      return key;
    });
    if (filterKeysChanged) {
      updates.filter_keys = nextFilterKeys;
    }

    // スコープ項目（scope_item_id）のラベル
    const scopeItemId = data.scope_item_id as string | undefined;
    if (scopeItemId) {
      const latest = labelMap.get(scopeItemId);
      if (latest !== undefined && latest !== data.scope_item_label) {
        updates.scope_item_label = latest;
      }
    }

    // 取得項目（column_mappings）の格納先ラベル
    const columnMappings = (data.column_mappings as ColumnMapping[] | undefined) ?? [];
    let columnsChanged = false;
    const nextColumns = columnMappings.map(cm => {
      const latest = cm.item_id ? labelMap.get(cm.item_id) : undefined;
      if (latest !== undefined && latest !== cm.item_label) {
        columnsChanged = true;
        return { ...cm, item_label: latest };
      }
      return cm;
    });
    if (columnsChanged) {
      updates.column_mappings = nextColumns;
    }

    // パターン名
    if (patternName !== undefined && patternName !== data.pattern_name) {
      updates.pattern_name = patternName;
    }

    if (Object.keys(updates).length > 0) {
      updates.updated_at = Timestamp.now();
      batch.update(mappingDoc.ref, updates);
      hasUpdate = true;
    }
  }

  if (hasUpdate) {
    await batch.commit();
  }

  // Cloud SQL 側の source_field_name もリネームする（照合キーの項目名が変わった場合）。
  // company_id はパターンドキュメントから取得する。Cloud SQL 未使用企業や
  // 旧名行なしの場合はバックエンド側で no-op。
  if (sourceFieldRenames.length > 0) {
    const patternSnap = await getDoc(doc(db, 'patterns', patternId));
    const gendriveCompanyId = (patternSnap.data()?.company_id as string) || '';
    if (gendriveCompanyId) {
      await renameMasterFieldMapping({
        gendrive_company_id: gendriveCompanyId,
        pattern_id: patternId,
        renames: sourceFieldRenames,
      });
    }
  }
};
